import classNames from 'classnames/bind';
import dayjs from 'dayjs';
import { i18nKeys, useTranslation } from 'locales';
import React, { useEffect, useState } from 'react';
import MoveRecoveryPlanInvoices from 'shared/action-component/MoveRecoveryPlanInvoices';
import Amount from 'shared/components/Amount';
import AYSModal from 'shared/components/AYSModal';
import { IconName } from 'shared/components/Icon';
import { useUseMatching } from 'shared/hooks/use-use-matching';
import { Button, ButtonColor } from 'shared/io';
import { success } from 'shared/notifications';
import { treatInvoice } from 'shared/serializer';
import { useLocalizedCurrencyFormatter } from 'shared/utils/currency';
import { isPaid } from 'shared/utils/invoice-status';
import { useGetConstants } from 'shared/utils/selectors';
import { creditNoteActions } from 'store/creditNote/creditnote.actions';
import { invoiceActions } from 'store/invoice/invoice.actions';
import { transactionActions } from 'store/transaction/transaction.actions';
import { DialogShowId, DialogShowSize, dialogHide, showDialog } from 'store/view/view.actions';
import type { CreditNote } from 'types/store/credit-note-state';
import type { ExternalPayment, Invoice, ManualPayment } from 'types/store/invoice-state';

import styleIdentifiers from './UnmatchPayment.module.scss';

const styles = classNames.bind(styleIdentifiers);

type Props = {
  reload: VoidFunction;
  invoice: Invoice;
};

export default function UnmatchPayment({ reload, invoice }: Props) {
  const { t } = useTranslation();
  const isMatchingActive = useUseMatching();
  const formatter = useLocalizedCurrencyFormatter();
  const constants = useGetConstants();
  const [payments, setPayments] = useState<any>();

  const isTpAccountingPayment = (payment: ManualPayment) =>
    constants.payment_modalities_tp_accounting.includes(payment.modality);

  const { manual_payments, external_payments } = payments || invoice;

  useEffect(() => {
    if (!invoice.external_payments) {
      invoiceActions.detail({
        id: invoice.id,
        callback: ({ data }: any) => {
          setPayments(treatInvoice(data));
        },
      });
    }
  }, []);

  const submit = (isExternalPayment: any, payment: any, steps_switch?: any) => {
    (isExternalPayment
      ? transactionActions.unmatchExternalPayment
      : transactionActions.unmatchManualPayment)({
      id: payment.id,
      keepMountOnExit: true,
      data: {
        recovery_step_id: steps_switch ? steps_switch[0].to : undefined,
      },
      callback: () => {
        dialogHide(DialogShowId.CUSTOM);
        reload();
        success({
          text: isExternalPayment
            ? 'INVOICE.UNMATCH_PAYMENT.SUCCESSFULLY_UNMATCH_PAYMENT'
            : 'INVOICE.UNMATCH_PAYMENT.SUCCESSFULLY_DELETE_PAYMENT',
        });
      },
    });
  };

  const unmatchPayment =
    (payment: ManualPayment | ExternalPayment, isExternalPayment?: boolean) => () => {
      if (isPaid(invoice)) {
        showDialog({
          id: DialogShowId.CONFIRM,
          size: DialogShowSize.SMALL,
          title: t(i18nKeys.CONFIRM),
          children: (
            <AYSModal
              text={t(i18nKeys.AYS.INVOICE_BACK_IN_PLAN)}
              confirmButtonText={t(i18nKeys.YES)}
              cancelButtonText={t(i18nKeys.NO)}
              onConfirm={() => {
                MoveRecoveryPlanInvoices({
                  title: t(i18nKeys.PLAN.INVOICES),
                  newPlanId: invoice.recovery_plan_id,
                  oldPlanId: invoice.recovery_plan_id,
                  currentStep: -1,
                  onSubmit: (steps_switch: any) => {
                    submit(isExternalPayment, payment, steps_switch);
                  },
                });
              }}
              onCancel={() => {
                submit(isExternalPayment, payment);
              }}
            />
          ),
        });
      } else {
        showDialog({
          id: DialogShowId.CONFIRM,
          size: DialogShowSize.SMALL,
          title: t(i18nKeys.CONFIRM),
          children: (
            <AYSModal
              text={
                <div>
                  <div>
                    {isMatchingActive &&
                      t(i18nKeys.INVOICE.UNMATCH_PAYMENT.RECONCILIATION_NOT_ACTIVE_ANYMORE)}
                  </div>
                  {t(
                    isExternalPayment
                      ? i18nKeys.INVOICE.UNMATCH_PAYMENT.AYS_TO_UNMATCH
                      : i18nKeys.INVOICE.UNMATCH_PAYMENT.AYS_TO_DELETE,
                  )}
                </div>
              }
              onConfirm={() => {
                submit(isExternalPayment, payment);
              }}
            />
          ),
        });
      }
    };

  const unlinkCn = (cn: CreditNote) => () => {
    creditNoteActions.unlinkToInvoices({
      id: cn.id,
      data: {
        unlink_invoice: invoice.id,
      },
      callback: () => {
        dialogHide(DialogShowId.CUSTOM);
        reload();
      },
    });
  };

  return (
    <div className={styles('UnmatchPayment')}>
      {external_payments?.length > 0 && (
        <div className="recovr-table">
          <table>
            <thead>
              <tr>
                <th>
                  <div>{t(i18nKeys.FORM.HISTORY.SENDER)}</div>
                </th>
                <th>
                  <div>{t(i18nKeys.DATE)}</div>
                </th>
                <th style={{ width: 120 }}>
                  <div style={{ whiteSpace: 'nowrap' }}>
                    {t(i18nKeys.INVOICE.UNMATCH_PAYMENT.PAYMENT_AMOUNT)}
                  </div>
                </th>
                <th>
                  <div>{t(i18nKeys.INVOICE.UNMATCH_PAYMENT.RECONCILIATION_AMOUNT)}</div>
                </th>
                <th>
                  <div>{t(i18nKeys.COMMUNICATION)}</div>
                </th>
                {!isMatchingActive && (
                  <th style={{ width: 150 }}>
                    <div>{t(i18nKeys.FORM.ACTIONS)}</div>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {external_payments.map((payment: any) => (
                <tr key={payment.id}>
                  <td>{payment.payment.counterparty_name || payment.payment.external_id}</td>
                  <td>{dayjs(payment.payment.value_date).format('L')}</td>
                  <td>
                    <Amount
                      localizedValue={payment.payment.amount}
                      value={payment.payment.localized_money_object.amount}
                      suffix={payment.payment.currency}
                    />
                  </td>
                  <td>
                    <Amount
                      localizedValue={payment.amount}
                      value={payment.localized_money_object?.amount}
                      suffix={payment.currency}
                    />
                  </td>
                  <td>{payment.payment.communication}</td>
                  {!isMatchingActive && (
                    <td className="actions">
                      <Button
                        className={styles('delete-payment')}
                        label={t(i18nKeys.INVOICE.UNMATCH_PAYMENT.CANCEL_PAYMENT)}
                        onClick={unmatchPayment(payment, true)}
                        small
                        noMargin
                        noShadow
                        color={ButtonColor.GREY}
                        iconLeft={IconName.UNLINK}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {manual_payments?.length > 0 && (
        <div className="recovr-table">
          <table>
            <thead>
              <tr>
                <th>
                  <div>{t(i18nKeys.MODALITY)}</div>
                </th>
                <th>
                  <div>{t(i18nKeys.DATE)}</div>
                </th>
                <th>
                  <div>{t(i18nKeys.AMOUNT)}</div>
                </th>
                <th>
                  <div>{t(i18nKeys.COMMENT)}</div>
                </th>
                <th style={{ width: 150 }}>
                  <div>{t(i18nKeys.FORM.ACTIONS)}</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {manual_payments.map((payment: any) => (
                <tr key={payment.id}>
                  <td>{payment.payment_modality_translated}</td>
                  <td>{dayjs(payment.received_at).format('L')}</td>
                  <td>
                    <Amount
                      localizedValue={payment.amount}
                      value={payment.localized_money_object?.amount}
                      suffix={payment.currency}
                    />
                  </td>
                  <td>{payment.notes}</td>
                  <td className="actions">
                    {!isTpAccountingPayment(payment) && (
                      <Button
                        className={styles('delete-payment')}
                        label={t(i18nKeys.DELETE)}
                        onClick={unmatchPayment(payment)}
                        small
                        noMargin
                        color={ButtonColor.GREY}
                        iconLeft={IconName.TRASH_SIMPLE}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {invoice.credit_notes_attributes.length > 0 && (
        <div className="recovr-table">
          <table>
            <thead>
              <tr>
                <th>
                  <div>{t(i18nKeys.FORM.REFERENCE)}</div>
                </th>
                <th>
                  <div>{t(i18nKeys.ISSUE_DATE)}</div>
                </th>
                <th>
                  <div>{t(i18nKeys.AMOUNT)}</div>
                </th>
                <th>
                  <div>{t(i18nKeys.STATUS)}</div>
                </th>
                {!isMatchingActive && (
                  <th style={{ width: 150 }}>
                    <div>{t(i18nKeys.FORM.ACTIONS)}</div>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {invoice.credit_notes_attributes.map(cn => {
                const matched = cn.matched_payments_attributes.filter(
                  e => e.invoice_attributes.id === invoice.id,
                );
                return (
                  <React.Fragment key={cn.id}>
                    {matched.map(matchedPayment => (
                      <tr key={matchedPayment.id}>
                        <td>{cn.reference}</td>
                        <td>{dayjs(cn.issue_date.toISOString()).format('L')}</td>
                        <td>{formatter.format(matchedPayment.amount)}</td>
                        <td>{cn.status}</td>
                        {!isMatchingActive && (
                          <td>
                            <Button
                              className={styles('delete-payment')}
                              label={t(i18nKeys.DELETE)}
                              onClick={unlinkCn(cn)}
                              small
                              noMargin
                              color={ButtonColor.GREY}
                              iconLeft={IconName.TRASH_SIMPLE}
                            />
                          </td>
                        )}
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
